import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ForumIcon from '@mui/icons-material/Forum';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import dayjs from 'dayjs';

import { BreadcrumbHeader, FilterTabs, DataTable, StatusBadge, EmptyState, ActionMenu, CreateVendorShipmentModal } from '../../components/shared';
import { partReplenishmentOrders, suppliers, warehouses, parts, purchaseOrders as poArray, partReplenishmentMessages } from '../../services/dataClient';

// Break at the last space within `max` chars, or hard-cut if no space
import ContentCard from "../../components/shared/ContentCard";function truncate(text, max = 14) {
  if (!text || text.length <= max) return { short: text, truncated: false };
  const breakAt = text.lastIndexOf(' ', max);
  const short = (breakAt > 4 ? text.slice(0, breakAt) : text.slice(0, max)) + '…';
  return { short, truncated: true };
}

const TABS = [
{ label: 'All', value: 'All' },
{ label: 'Active', value: 'Active' },
{ label: 'History', value: 'History' }];

const EMPTY_LINE_ITEM = { partNumber: '', partDescription: '', quantity: '1', unitCost: '' };

const EMPTY_FORM = {
  vendor: '',
  stockLocation: '',
  lineItems: [{ ...EMPTY_LINE_ITEM }],
  notes: ''
};

const activeSuppliers = suppliers.filter((s) => s.active);
const activeWarehouses = warehouses.filter((w) => w.active);

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'var(--grey-200)' },
    '&.Mui-focused fieldset': { borderColor: '#009688' }
  }
};

const EMPTY_PO_LINE = { partNumber: '', partDescription: '', quantity: '1', unitCost: '' };

// ─── Create PO Modal (for linking a new PO to a replenishment order) ──────────
function CreatePOModal({ open, order, onClose, onSave }) {
  const [vendorName, setVendorName] = useState('');
  const [lineItems, setLineItems] = useState([{ ...EMPTY_PO_LINE }]);
  const [notes, setNotes] = useState('');

  const handleEnter = () => {
    setVendorName(order?.vendorName ?? '');
    const seed = order?.lineItems?.length ?
    order.lineItems.map((li) => ({
      partNumber: li.partNumber ?? '',
      partDescription: li.partDescription ?? '',
      quantity: String(li.quantity ?? 1),
      unitCost: li.unitCost != null ? String(li.unitCost) : ''
    })) :
    [{ partNumber: order?.partNumber ?? '', partDescription: order?.partDescription ?? '', quantity: '1', unitCost: '' }];
    setLineItems(seed);
    setNotes('');
  };

  const updateLine = (idx, field, value) =>
  setLineItems((prev) => prev.map((li, i) => i === idx ? { ...li, [field]: value } : li));

  const handlePartChange = (idx, value) => {
    const desc = parts.find((p) => p.partNumber === value)?.description ?? '';
    setLineItems((prev) => prev.map((li, i) => i === idx ? { ...li, partNumber: value, partDescription: desc } : li));
  };

  const addLine = () => setLineItems((prev) => [...prev, { ...EMPTY_PO_LINE }]);
  const removeLine = (idx) => setLineItems((prev) => prev.filter((_, i) => i !== idx));

  const isValid = vendorName && lineItems.every((li) => li.partNumber && parseInt(li.quantity, 10) >= 1);

  const handleSave = () => {
    const built = lineItems.map((li) => ({
      partNumber: li.partNumber,
      partDescription: li.partDescription,
      quantity: parseInt(li.quantity, 10) || 1,
      unitCost: li.unitCost !== '' ? parseFloat(li.unitCost) : null
    }));
    const totalCost = built.reduce((s, li) => s + (li.unitCost ?? 0) * li.quantity, 0);
    const newPO = {
      id: `po-rep-${Date.now()}`,
      poNumber: `PO-${6000 + poArray.length + 1}`,
      vendorName,
      partNumber: built[0].partNumber,
      partDescription: built[0].partDescription,
      quantity: built[0].quantity,
      unitCost: built[0].unitCost ?? 0,
      totalCost,
      lineItems: built,
      status: 'Draft',
      date: dayjs().format('YYYY-MM-DD'),
      notes: notes || null,
      linkedOrderId: order?.id ?? null,
      linkedTicketNumber: order?.orderNumber ?? null
    };
    poArray.unshift(newPO);
    onSave(newPO);
  };

  const grandTotal = lineItems.reduce(
    (s, li) => s + (parseFloat(li.unitCost) || 0) * (parseInt(li.quantity, 10) || 0), 0
  );

  return (
    <Dialog open={open} onClose={(e, r) => {if (r !== 'backdropClick') onClose();}} maxWidth="md" fullWidth
    TransitionProps={{ onEnter: handleEnter }}>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Create Purchase Order
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 0, pt: '12px !important' }}>
        {/* Context bar */}
        {order &&
        <Box sx={{ backgroundColor: '#F5F5F5', borderRadius: '6px', px: 2, py: 1.25, mb: 2, display: 'flex', gap: 3 }}>
            <Box>
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Order #</Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-900)' }}>{order.orderNumber}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stock Location</Typography>
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>{order.stockLocation ?? '—'}</Typography>
            </Box>
          </Box>
        }

        {/* Supplier */}
        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>Supplier *</Typography>
        <FormControl size="small" fullWidth sx={{ ...fieldSx, mb: 2.5 }}>
          <InputLabel>Supplier</InputLabel>
          <Select label="Supplier" value={vendorName} onChange={(e) => setVendorName(e.target.value)}>
            {activeSuppliers.map((s) =>
            <MenuItem key={s.id} value={s.name} sx={{ fontSize: '0.875rem' }}>{s.name}</MenuItem>
            )}
          </Select>
        </FormControl>
        <Divider sx={{ mb: 2.5 }} />

        {/* Parts */}
        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.25 }}>Parts *</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1 }}>
          {lineItems.map((li, idx) => {
            const rowTotal = (parseFloat(li.unitCost) || 0) * (parseInt(li.quantity, 10) || 0);
            return (
              <Box key={idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <FormControl size="small" required sx={{ flex: '1.2 1 0', ...fieldSx }}>
                  <InputLabel>Part #</InputLabel>
                  <Select label="Part #" value={li.partNumber} onChange={(e) => handlePartChange(idx, e.target.value)}>
                    {parts.map((p) =>
                    <MenuItem key={p.partNumber} value={p.partNumber} sx={{ fontSize: '0.875rem' }}>{p.partNumber}</MenuItem>
                    )}
                  </Select>
                </FormControl>
                <TextField size="small" label="Description" value={li.partDescription}
                onChange={(e) => updateLine(idx, 'partDescription', e.target.value)}
                sx={{ flex: '1.8 1 0', ...fieldSx }} />
                <TextField size="small" label="Qty" required type="number" value={li.quantity}
                onChange={(e) => updateLine(idx, 'quantity', e.target.value)}
                inputProps={{ min: 1 }} sx={{ flex: '0.5 1 0', minWidth: 64, ...fieldSx }} />
                <TextField size="small" label="Unit Cost ($)" type="number" value={li.unitCost}
                onChange={(e) => updateLine(idx, 'unitCost', e.target.value)}
                inputProps={{ min: 0, step: '0.01' }}
                helperText={rowTotal > 0 ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(rowTotal) : ''}
                sx={{ flex: '0.8 1 0', minWidth: 100, ...fieldSx }} />
                <Box sx={{ pt: '4px' }}>
                  <IconButton size="small" onClick={() => removeLine(idx)} disabled={lineItems.length === 1}
                  sx={{ color: lineItems.length === 1 ? 'transparent' : 'var(--grey-400)', '&:hover': { color: '#d32f2f' } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>);

          })}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={addLine}
          sx={{ color: '#009688', textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', p: 0, '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } }}>
            Add Part
          </Button>
          {grandTotal > 0 &&
          <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-600)' }}>
              Total: <strong>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(grandTotal)}</strong>
            </Typography>
          }
        </Box>
        <Divider sx={{ mb: 2.5 }} />

        {/* Notes */}
        <TextField size="small" label="Notes / Special Instructions (optional)" value={notes}
        onChange={(e) => setNotes(e.target.value)} multiline rows={2} fullWidth sx={fieldSx} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography onClick={onClose} sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
          Cancel
        </Typography>
        <Button variant="contained" size="small" disabled={!isValid} onClick={handleSave}
        sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' }, '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' }, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none', px: 2 }}>
          Create PO
        </Button>
      </DialogActions>
    </Dialog>);

}

// ─── Chat helpers ─────────────────────────────────────────────────────────────
function resolveChatRecipient(order) {
  if (!order) return null;
  const sup = suppliers.find((s) => s.name === order.vendorName);
  return sup ? { name: sup.contactName, email: sup.contactEmail } : null;
}

function ChatBubbleIcon({ messages, onClick }) {
  const last = messages[messages.length - 1];
  const color = messages.length === 0 ?
  'var(--grey-300)' :
  last.sender === 'contact' ? '#1565C0' : '#009688';
  const tooltip = messages.length === 0 ?
  'No messages yet — click to chat' :
  `${last.senderName}: ${last.text.slice(0, 60)}${last.text.length > 60 ? '…' : ''}`;
  return (
    <Tooltip title={tooltip} placement="top" arrow enterDelay={200}>
      <ForumIcon
        onClick={onClick}
        sx={{ fontSize: 17, color, display: 'block', cursor: 'pointer', '&:hover': { opacity: 0.75 } }} />
      
    </Tooltip>);

}

function ModalChatBody({ messages, recipient, onSend }) {
  const [inputText, setInputText] = useState('');
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const handleSend = () => {
    const text = inputText.trim();
    if (!text || !recipient) return;
    onSend(text);
    setInputText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {e.preventDefault();handleSend();}
  };

  const fmtTime = (iso) => dayjs(iso).format('MMM D, h:mm A');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Scroll area */}
      <Box
        ref={scrollRef}
        sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2,
          display: 'flex', flexDirection: 'column', gap: '12px',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { borderRadius: '4px', backgroundColor: 'var(--grey-300)' }
        }}>
        
        {recipient && messages.length === 0 &&
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)', fontStyle: 'italic', textAlign: 'center' }}>
              No messages yet. Start the conversation.
            </Typography>
          </Box>
        }
        {!recipient &&
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)', fontStyle: 'italic', textAlign: 'center', px: 2 }}>
              No supplier assigned to this order.
            </Typography>
          </Box>
        }
        {messages.map((msg) => {
          const isMe = msg.sender === 'coordinator';
          return (
            <Box key={msg.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
              <Box sx={{ maxWidth: '80%', px: '12px', py: '8px',
                borderRadius: isMe ? '8px 8px 2px 8px' : '8px 8px 8px 2px',
                backgroundColor: isMe ? '#009688' : 'var(--grey-100)',
                color: isMe ? '#fff' : 'var(--grey-900)'
              }}>
                <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.45, wordBreak: 'break-word' }}>
                  {msg.text}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.6875rem', color: 'var(--grey-400)', mt: '3px', px: '2px' }}>
                {fmtTime(msg.sentAt)}
              </Typography>
            </Box>);

        })}
      </Box>

      {/* Input row */}
      <Box sx={{ borderTop: '1px solid var(--grey-200)', display: 'flex', alignItems: 'center', gap: '6px' }}>
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} />
        <IconButton
          size="small" disabled={!recipient}
          onClick={() => fileInputRef.current?.click()}
          sx={{ flexShrink: 0, ml: '10px', color: 'var(--grey-400)', '&:hover': { color: '#009688', backgroundColor: 'transparent' }, '&.Mui-disabled': { color: 'var(--grey-200)' } }}>
          
          <AttachFileIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <TextField
          size="small" placeholder="Type a message…" multiline maxRows={3} fullWidth
          disabled={!recipient} value={inputText}
          onChange={(e) => setInputText(e.target.value)} onKeyDown={handleKeyDown}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem', borderRadius: 0, '& fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: 'none' } },
            '& .MuiInputBase-input': { py: '10px', px: '4px' }
          }} />
        
        <IconButton
          size="small" disabled={!inputText.trim() || !recipient} onClick={handleSend}
          sx={{ flexShrink: 0, mr: '10px', backgroundColor: '#009688', color: '#fff', borderRadius: '8px', width: 32, height: 32, '&:hover': { backgroundColor: '#00796B' }, '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-400)' } }}>
          
          <SendIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>
    </Box>);

}

export default function PartReplenishmentPage() {
  const navigate = useNavigate();

  const [orders, setOrders] = useState(partReplenishmentOrders);
  const [activeTab, setActiveTab] = useState('All');
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: 'createdDate', direction: 'desc' });
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerMessage, setBannerMessage] = useState('');

  // Create Vendor Shipment from order
  const [createVsOpen, setCreateVsOpen] = useState(false);
  const [vsTarget, setVsTarget] = useState(null);

  // Create PO from order
  const [createPoOpen, setCreatePoOpen] = useState(false);
  const [poTarget, setPoTarget] = useState(null);

  // Chat modal state
  const [chatRow, setChatRow] = useState(null);
  const [messagesMap, setMessagesMap] = useState(() => {
    const map = {};
    partReplenishmentMessages.forEach((m) => {
      if (!map[m.replenishmentId]) map[m.replenishmentId] = [];
      map[m.replenishmentId].push(m);
    });
    return map;
  });

  const chatRecipient = chatRow ? resolveChatRecipient(chatRow) : null;
  const chatMessages = chatRow ? messagesMap[chatRow.id] ?? [] : [];

  const handleChatSend = (text) => {
    if (!chatRow) return;
    const msg = { id: `rep-msg-local-${Date.now()}`, replenishmentId: chatRow.id, sender: 'coordinator', senderName: 'You', text, sentAt: new Date().toISOString() };
    setMessagesMap((prev) => ({ ...prev, [chatRow.id]: [...(prev[chatRow.id] ?? []), msg] }));
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFormChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const updateLineItem = (idx, field, value) =>
  setForm((prev) => ({
    ...prev,
    lineItems: prev.lineItems.map((item, i) => i === idx ? { ...item, [field]: value } : item)
  }));

  const addLineItem = () =>
  setForm((prev) => ({ ...prev, lineItems: [...prev.lineItems, { ...EMPTY_LINE_ITEM }] }));

  const removeLineItem = (idx) =>
  setForm((prev) => ({ ...prev, lineItems: prev.lineItems.filter((_, i) => i !== idx) }));

  const handleOpenModal = () => {
    setForm({ ...EMPTY_FORM, lineItems: [{ ...EMPTY_LINE_ITEM }] });
    setModalOpen(true);
  };

  const handleSubmit = () => {
    const nextNum = orders.length + 1;
    const firstItem = form.lineItems[0] ?? {};
    const builtLineItems = form.lineItems.map((li) => ({
      partNumber: li.partNumber,
      partDescription: li.partDescription,
      quantity: parseFloat(li.quantity) || 0,
      unitCost: parseFloat(li.unitCost) || null
    }));
    const orderId = `so-new-${Date.now()}`;
    const orderNum = `REP${String(5000 + nextNum).padStart(4, '0')}`;

    // Auto-create a linked PO in Draft status
    const totalCost = builtLineItems.reduce((s, li) => s + li.quantity * (li.unitCost ?? 0), 0);
    const newPO = {
      id: `po-new-${Date.now() + 1}`,
      poNumber: `PO-${6000 + poArray.length + 1}`,
      vendorName: form.vendor,
      partNumber: firstItem.partNumber,
      partDescription: firstItem.partDescription,
      quantity: builtLineItems[0]?.quantity ?? 0,
      unitCost: builtLineItems[0]?.unitCost ?? 0,
      totalCost,
      lineItems: builtLineItems,
      status: 'Draft',
      date: dayjs().format('YYYY-MM-DD'),
      notes: form.notes || null,
      linkedOrderId: orderId,
      linkedTicketNumber: orderNum
    };
    poArray.unshift(newPO);

    const newOrder = {
      id: orderId,
      orderNumber: orderNum,
      vendorName: form.vendor,
      stockLocation: form.stockLocation,
      partNumber: firstItem.partNumber,
      partDescription: firstItem.partDescription,
      lineItems: builtLineItems,
      customerDestination: '',
      customerName: '',
      customerAddress: '',
      notes: form.notes,
      status: 'Active',
      createdDate: dayjs().format('YYYY-MM-DD'),
      vendorShipmentId: null,
      vendorShipmentNumber: null,
      linkedPurchaseOrderId: newPO.id,
      linkedPurchaseOrderNumber: newPO.poNumber
    };
    setOrders((prev) => [newOrder, ...prev]);
    setModalOpen(false);
    setBannerMessage(`Replenishment order ${orderNum} created with linked PO ${newPO.poNumber}.`);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 4000);
  };

  const isFormValid = form.vendor && form.stockLocation &&
  form.lineItems.length > 0 &&
  form.lineItems.every((li) => li.partNumber.trim() && parseFloat(li.quantity) >= 1);

  // ── Create Vendor Shipment from a part replenishment order row ───────────────
  const handleOpenCreateVS = (order) => {
    setVsTarget(order);
    setCreateVsOpen(true);
  };

  const handleOpenCreatePO = (order) => {
    setPoTarget(order);
    setCreatePoOpen(true);
  };

  const handleSubmitPO = (newPO) => {
    setOrders((prev) =>
    prev.map((o) =>
    o.id === poTarget.id ?
    { ...o, linkedPurchaseOrderId: newPO.id, linkedPurchaseOrderNumber: newPO.poNumber } :
    o
    )
    );
    setCreatePoOpen(false);
    setBannerMessage(`Purchase order ${newPO.poNumber} created and linked to ${poTarget.orderNumber}.`);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 4000);
  };

  const handleSubmitVS = (newShipment) => {
    setOrders((prev) =>
    prev.map((o) =>
    o.id === vsTarget.id ?
    { ...o, vendorShipmentId: newShipment.id, vendorShipmentNumber: newShipment.shipmentNumber } :
    o
    )
    );
    setCreateVsOpen(false);
    setBannerMessage(`Replenishment ${newShipment.shipmentNumber} created and linked to ${vsTarget.orderNumber}.`);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 4000);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders
    .filter((o) => activeTab === 'All' || o.status === activeTab)
    .filter((o) =>
    !q ||
    o.orderNumber.toLowerCase().includes(q) ||
    o.vendorName.toLowerCase().includes(q) ||
    (o.stockLocation ?? '').toLowerCase().includes(q)
    )
    .sort((a, b) => {
      const va = a[sortConfig.field] ?? '';
      const vb = b[sortConfig.field] ?? '';
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
  }, [orders, activeTab, search, sortConfig]);

  const columns = [
  {
    field: 'orderNumber',
    headerName: 'Order #',
    sortable: true,
    renderCell: (row) =>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <Typography
        onClick={(e) => {e.stopPropagation();navigate(`/orders/part-replenishment/${row.id}`);}}
        sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
        
            {row.orderNumber}
          </Typography>
          {row.linkedPurchaseOrderNumber &&
      <Typography
        onClick={(e) => {e.stopPropagation();navigate(`/accounting/purchase-orders/${row.linkedPurchaseOrderId}`);}}
        sx={{ fontSize: '0.7rem', color: 'var(--grey-400)', cursor: 'pointer', '&:hover': { color: '#009688', textDecoration: 'underline' } }}>
        
              {row.linkedPurchaseOrderNumber}
            </Typography>
      }
        </Box>

  },
  {
    field: 'vendorName',
    headerName: 'Supplier',
    sortable: true,
    renderCell: (row) => {
      const { short, truncated } = truncate(row.vendorName);
      return (
        <Tooltip title={truncated ? row.vendorName : ''} placement="top" arrow enterDelay={300} disableHoverListener={!truncated}>
            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>{short ?? '—'}</Typography>
          </Tooltip>);

    }
  },
  {
    field: 'partNumber',
    headerName: 'Part #',
    sortable: true,
    renderCell: (row) => {
      const extraCount = (row.lineItems?.length ?? 1) - 1;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={{ fontSize: '0.875rem' }}>{row.partNumber ?? '—'}</Typography>
            {extraCount > 0 &&
          <Box sx={{ px: '6px', py: '1px', borderRadius: '4px', backgroundColor: 'var(--grey-100)', border: '1px solid var(--grey-200)' }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--grey-500)', whiteSpace: 'nowrap' }}>
                  +{extraCount} more
                </Typography>
              </Box>
          }
          </Box>);

    }
  },
  {
    field: 'customerDestination',
    headerName: 'Stock Location',
    sortable: true,
    renderCell: (row) => {
      const wh = warehouses.find((w) => w.name === row.stockLocation);
      const typeColors = {
        FSL: { bg: '#E3F2FD', color: '#1565C0' },
        Warehouse: { bg: '#E0F2F1', color: '#00695C' },
        'On-Site': { bg: '#FFF3E0', color: '#E65100' }
      };
      const tc = typeColors[wh?.type] ?? { bg: 'var(--grey-100)', color: 'var(--grey-600)' };
      const { short: locShort, truncated: locTrunc } = truncate(row.stockLocation, 16);
      return (
        <Tooltip title={locTrunc ? row.stockLocation : ''} placement="top" arrow enterDelay={300} disableHoverListener={!locTrunc}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>
                {locShort || '—'}
              </Typography>
              {wh?.type &&
            <Box sx={{ px: '8px', py: '2px', borderRadius: '10px', backgroundColor: tc.bg }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: tc.color, lineHeight: 1, whiteSpace: 'nowrap' }}>
                    {wh.type}
                  </Typography>
                </Box>
            }
            </Box>
          </Tooltip>);

    }
  },
  { field: 'createdDate', headerName: 'Created Date', sortable: true },
  {
    field: 'vendorShipmentNumber',
    headerName: 'Shipment #',
    sortable: false,
    renderCell: (row) =>
    row.vendorShipmentNumber ?
    <Typography
      onClick={(e) => {e.stopPropagation();navigate(`/shipments/replenishment-shipments/${row.vendorShipmentId}`);}}
      sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
      
            {row.vendorShipmentNumber}
          </Typography> :

    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>

  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: (row) => <StatusBadge status={row.status} />
  },
  {
    field: '_notif',
    headerName: '',
    sortable: false,
    renderCell: (row) =>
    <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center' }}>
          <ChatBubbleIcon
        messages={messagesMap[row.id] ?? []}
        onClick={() => setChatRow(row)} />
      
        </Box>

  },
  {
    field: '_actions',
    headerName: '',
    sortable: false,
    renderCell: (row) => {
      const actions = [
      { label: 'View Detail', onClick: () => navigate(`/orders/part-replenishment/${row.id}`) },
      ...(row.vendorShipmentId ?
      [{ label: 'View Replenishment', onClick: () => navigate(`/shipments/replenishment-shipments/${row.vendorShipmentId}`) }] :
      [{ label: 'Create Replenishment Shipment', onClick: () => handleOpenCreateVS(row) }]),
      ...(row.linkedPurchaseOrderId ?
      [{ label: 'View PO', onClick: () => navigate(`/accounting/purchase-orders/${row.linkedPurchaseOrderId}`) }] :
      [{ label: 'Create PO', onClick: () => handleOpenCreatePO(row) }])];

      return (
        <Box onClick={(e) => e.stopPropagation()}>
            <ActionMenu actions={actions} hideUntilRowHover />
          </Box>);

    }
  }];

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Success banner */}
      {showBanner &&
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '10px',
          backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7',
          borderRadius: '8px', px: 2, py: '12px'
        }}>
        
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>
            {bannerMessage}
          </Typography>
        </Box>
      }

      <BreadcrumbHeader
        title="Part Replenishment"
        crumbs={[
        { label: 'Orders' },
        { label: 'Part Replenishment' }]
        } />

      <ContentCard sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FilterTabs tabs={TABS} value={activeTab} onChange={setActiveTab} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
          <TextField
            size="small"
            placeholder="Search by Order #, Vendor, or Customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment:
              <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'var(--grey-400)' }} />
                </InputAdornment>

            }}
            sx={{ minWidth: 400, maxWidth: 400, backgroundColor: 'var(--paper)', borderRadius: 1 }} />
          
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
            sx={{ backgroundColor: '#009688',
              '&:hover': { backgroundColor: '#00796B' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: 'none'
            }}>
            
            Create Part Replenishment
          </Button>
        </Box>

        <Box sx={{ borderRadius: '8px', border: '1px solid var(--grey-200)', overflow: 'hidden' }}>
          {filtered.length === 0 ?
          <EmptyState title="No orders found" description="Try adjusting your filter or search query." /> :

          <DataTable
            columns={columns}
            data={filtered}
            onRowClick={(row) => navigate(`/orders/part-replenishment/${row.id}`)}
            sortConfig={sortConfig}
            onSort={handleSort} />

          }
        </Box>
      </ContentCard>

      {/* Create Part Replenishment modal */}
      <Dialog open={modalOpen} onClose={(e, reason) => {if (reason !== 'backdropClick') setModalOpen(false);}} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Create Part Replenishment
          <IconButton size="small" onClick={() => setModalOpen(false)} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
            <HighlightOffIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>

          {/* Supplier */}
          <FormControl size="small" fullWidth required sx={fieldSx}>
            <InputLabel>Supplier</InputLabel>
            <Select label="Supplier" value={form.vendor} onChange={handleFormChange('vendor')}>
              {activeSuppliers.map((s) =>
              <MenuItem key={s.id} value={s.name} sx={{ fontSize: '0.875rem' }}>{s.name}</MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Stock Location */}
          <FormControl size="small" fullWidth required sx={fieldSx}>
            <InputLabel>Stock Location</InputLabel>
            <Select label="Stock Location" value={form.stockLocation} onChange={handleFormChange('stockLocation')}>
              {activeWarehouses.map((w) =>
              <MenuItem key={w.id} value={w.name} sx={{ fontSize: '0.875rem' }}>
                  {w.name}
                  <Typography component="span" sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', ml: '6px' }}>
                    — {w.type}
                  </Typography>
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Parts */}
          <Box>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--grey-700)', mb: '8px' }}>
              Parts
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {form.lineItems.map((item, idx) =>
              <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <TextField
                  size="small" label="Part #" required value={item.partNumber}
                  onChange={(e) => updateLineItem(idx, 'partNumber', e.target.value)}
                  sx={{ ...fieldSx, flex: '1 1 0', minWidth: 0 }} />
                
                  <TextField
                  size="small" label="Description" value={item.partDescription}
                  onChange={(e) => updateLineItem(idx, 'partDescription', e.target.value)}
                  sx={{ ...fieldSx, flex: '1.4 1 0', minWidth: 0 }} />
                
                  <TextField
                  size="small" label="Qty" required type="number" value={item.quantity}
                  onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                  inputProps={{ min: 1 }}
                  sx={{ ...fieldSx, width: '70px', flexShrink: 0 }} />
                
                  <TextField
                  size="small" label="Cost ($)" type="number" value={item.unitCost}
                  onChange={(e) => updateLineItem(idx, 'unitCost', e.target.value)}
                  inputProps={{ min: 0, step: '0.01' }}
                  sx={{ ...fieldSx, width: '90px', flexShrink: 0 }} />
                
                  {form.lineItems.length > 1 ?
                <Tooltip title="Remove" placement="top" arrow enterDelay={300}>
                      <IconButton
                    size="small"
                    onClick={() => removeLineItem(idx)}
                    sx={{ mt: '4px', color: 'var(--grey-400)', '&:hover': { color: '#B71C1C' }, flexShrink: 0 }}>
                    
                        <HighlightOffIcon fontSize="small" />
                      </IconButton>
                    </Tooltip> :

                <Box sx={{ width: 28, flexShrink: 0 }} />
                }
                </Box>
              )}
            </Box>
            <Button
              size="small"
              onClick={addLineItem}
              sx={{ mt: '10px', textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600,
                color: '#009688', p: 0, minWidth: 0,
                '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
              }}>
              
              + Add Part
            </Button>
          </Box>

          {/* Notes */}
          <TextField
            size="small" label="Notes (optional)" value={form.notes}
            onChange={handleFormChange('notes')} multiline rows={2} fullWidth sx={fieldSx} />
          
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Typography
            onClick={() => setModalOpen(false)}
            sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
            
            Cancel
          </Typography>
          <Button
            variant="contained"
            size="small"
            disabled={!isFormValid}
            onClick={handleSubmit}
            sx={{ backgroundColor: '#009688',
              '&:hover': { backgroundColor: '#00796B' },
              '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: 'none',
              px: 2
            }}>
            
            Create Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Vendor Shipment — shared modal (prefilled from part replenishment order) */}
      {vsTarget &&
      <CreateVendorShipmentModal
        open={createVsOpen}
        onClose={() => setCreateVsOpen(false)}
        onSubmit={handleSubmitVS}
        prefill={{
          partReplenishmentOrderId: vsTarget.id,
          partReplenishmentOrderNumber: vsTarget.orderNumber,
          vendorName: vsTarget.vendorName,
          stockLocation: vsTarget.stockLocation ?? ''
        }} />

      }

      {/* Create PO — linked to replenishment order */}
      <CreatePOModal
        open={createPoOpen}
        order={poTarget}
        onClose={() => setCreatePoOpen(false)}
        onSave={handleSubmitPO} />

      {/* Chat modal */}
      <Dialog
        open={Boolean(chatRow)}
        onClose={(e, reason) => {if (reason !== 'backdropClick') setChatRow(null);}}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { height: 500, display: 'flex', flexDirection: 'column' } }}>
        
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)', lineHeight: 1.3 }}>
              Messages — {chatRow?.orderNumber}
            </Typography>
            {chatRecipient ?
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>
                {chatRecipient.name} · {chatRecipient.email}
              </Typography> :

            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)', mt: '2px', fontStyle: 'italic' }}>
                No supplier assigned
              </Typography>
            }
          </Box>
          <IconButton size="small" onClick={() => setChatRow(null)} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1, mt: '-2px' }}>
            <HighlightOffIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important', px: 0, pb: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <ModalChatBody messages={chatMessages} recipient={chatRecipient} onSend={handleChatSend} />
        </DialogContent>
      </Dialog>
    </Box>);

}